import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import BookingCalendar from './components/BookingCalendar';
import CurrencySelector from './components/CurrencySelector';
import BookingForm from './components/BookingForm';
import ConfirmationModal from './components/ConfirmationModal';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

const VERIFY_URL = `${supabaseUrl}/functions/v1/verify-flutterwave-payment`;

const EXCHANGE_RATES: Record<string, number> = {
  NGN: 1,
  USD: 0.00062,
  GBP: 0.00049,
  EUR: 0.00058,
  CAD: 0.00085,
  GHS: 0.0094,
  KES: 0.08,
  ZAR: 0.011,
};

export default function BookingPage() {
  const navigate = useNavigate();
  const [availableSlots, setAvailableSlots] = useState<
    { id: string; date: string; time_slot: string; is_available: boolean }[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState('NGN');
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<{
    date: string;
    time: string;
    name: string;
    email: string;
    amount: string;
    currency: string;
    txRef: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [flutterwavePublicKey, setFlutterwavePublicKey] = useState('');
  const [baseAmount, setBaseAmount] = useState(50000);
  const [baseCurrency, setBaseCurrency] = useState('NGN');

  useEffect(() => {
    fetchPaymentSettings();
    fetchAvailability();

    // Set up real-time subscription to availability changes
    const channel = supabase
      .channel('availability-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'availability',
        },
        () => {
          // Refresh availability when any change occurs
          fetchAvailability();
        }
      )
      .subscribe();

    // Also refresh every 30 seconds to catch any missed updates
    const intervalId = setInterval(() => {
      fetchAvailability();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(intervalId);
    };
  }, []);

  const fetchPaymentSettings = async () => {
    const { data, error } = await supabase
      .from('payment_settings')
      .select('flutterwave_public_key, consultation_fee, currency')
      .limit(1)
      .single();

    if (!error && data) {
      setFlutterwavePublicKey(data.flutterwave_public_key || '');
      setBaseAmount(Number(data.consultation_fee) || 50000);
      setBaseCurrency(data.currency || 'NGN');
      setSelectedCurrency(data.currency || 'NGN');
    }
  };

  const fetchAvailability = async () => {
    setIsLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('availability')
      .select('*')
      .gte('date', today)
      .eq('is_available', true)
      .order('date', { ascending: true });

    if (!error && data) {
      setAvailableSlots(data);
    }
    setIsLoading(false);
  };

  const convertCurrency = useCallback(
    async (currency: string) => {
      if (currency === baseCurrency) {
        setConvertedAmount(baseAmount);
        return;
      }
      setIsConverting(true);
      try {
        const response = await fetch(
          `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`
        );
        if (response.ok) {
          const data = await response.json();
          const rate = data.rates[currency];
          if (rate) {
            const converted = Math.ceil(baseAmount * rate * 100) / 100;
            setConvertedAmount(converted);
          } else {
            const fallbackRate = EXCHANGE_RATES[currency] || 1;
            setConvertedAmount(
              Math.ceil(baseAmount * fallbackRate * 100) / 100
            );
          }
        } else {
          const fallbackRate = EXCHANGE_RATES[currency] || 1;
          setConvertedAmount(
            Math.ceil(baseAmount * fallbackRate * 100) / 100
          );
        }
      } catch {
        const fallbackRate = EXCHANGE_RATES[currency] || 1;
        setConvertedAmount(
          Math.ceil(baseAmount * fallbackRate * 100) / 100
        );
      } finally {
        setIsConverting(false);
      }
    },
    [baseAmount, baseCurrency]
  );

  useEffect(() => {
    if (baseAmount > 0) {
      convertCurrency(selectedCurrency);
    }
  }, [selectedCurrency, baseAmount, convertCurrency]);

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const loadFlutterwaveScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.querySelector('script[src*="flutterwave"]')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.flutterwave.com/v3.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Flutterwave'));
      document.head.appendChild(script);
    });
  };

  const handleProceedToPayment = async () => {
    if (
      !selectedDate ||
      !selectedTime ||
      !formData.name ||
      !formData.email ||
      !formData.phone
    ) {
      return;
    }

    if (!flutterwavePublicKey) {
      setErrorMsg('Payment system is not configured. Please contact support.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      // Create booking in database first
      const txRef = `GRN-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 8)}`;
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          client_name: formData.name,
          client_email: formData.email,
          client_phone: formData.phone,
          booking_date: selectedDate,
          booking_time: selectedTime,
          status: 'pending',
          payment_status: 'unpaid',
          payment_id: txRef,
          message: formData.message || '',
          currency: selectedCurrency,
          amount_paid: convertedAmount || baseAmount,
        })
        .select();

      if (bookingError || !bookingData || bookingData.length === 0) {
        console.error('Booking error:', bookingError);
        setErrorMsg('Failed to create booking. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const booking = bookingData[0];

      // Load Flutterwave and initiate payment
      await loadFlutterwaveScript();

      const paymentAmount = convertedAmount || baseAmount;
      const paymentCurrency = selectedCurrency;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).FlutterwaveCheckout({
        public_key: flutterwavePublicKey,
        tx_ref: txRef,
        amount: paymentAmount,
        currency: paymentCurrency,
        payment_options: 'card,mobilemoney,ussd,banktransfer',
        customer: {
          email: formData.email,
          phone_number: formData.phone,
          name: formData.name,
        },
        customizations: {
          title: 'Greenify Consultation',
          description: `Consultation booking for ${new Date(
            selectedDate + 'T00:00:00'
          ).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}`,
          logo: '',
        },
        callback: async (response: {
          status: string;
          transaction_id: number;
          tx_ref: string;
        }) => {
          if (response.status === 'successful') {
            try {
              const verifyRes = await fetch(VERIFY_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  transaction_id: response.transaction_id,
                  booking_id: booking.id,
                }),
              });

              if (verifyRes.ok) {
                // Send form notification email only after successful payment
                const formBody = new URLSearchParams();
                formBody.append('name', formData.name);
                formBody.append('email', formData.email);
                formBody.append('phone', formData.phone);
                formBody.append('message', formData.message);
                formBody.append('booking_date', selectedDate);
                formBody.append('booking_time', selectedTime);
                formBody.append('currency', selectedCurrency);
                formBody.append(
                  'amount',
                  String(convertedAmount || baseAmount)
                );
                formBody.append('payment_reference', response.tx_ref);

                fetch('https://readdy.ai/api/form/d6afs0tbiueuoqm1sfug', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                  body: formBody.toString(),
                }).catch(() => {});

                setBookingDetails({
                  date: selectedDate,
                  time: selectedTime,
                  name: formData.name,
                  email: formData.email,
                  amount: paymentAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  currency: paymentCurrency,
                  txRef: response.tx_ref,
                });
                setShowConfirmation(true);
              } else {
                setErrorMsg(
                  'Payment received but verification pending. Please contact support with reference: ' +
                    response.tx_ref
                );
              }
            } catch {
              setErrorMsg(
                'Payment received but verification pending. Please contact support with reference: ' +
                  response.tx_ref
              );
            }
          } else {
            setErrorMsg('Payment was not successful. Please try again.');
            await supabase
              .from('bookings')
              .update({ payment_status: 'unpaid', status: 'cancelled' })
              .eq('id', booking.id);
          }
          setIsSubmitting(false);
        },
        onclose: () => {
          setIsSubmitting(false);
        },
      });
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMsg('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const isReady =
    !!selectedDate &&
    !!selectedTime &&
    !!formData.name.trim() &&
    !!formData.email.trim() &&
    !!formData.phone.trim();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Back to Home
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <i className="ri-leaf-line text-white text-sm"></i>
            </div>
            <span className="text-sm font-bold text-slate-900">GREENIFY</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-slate-900 py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-xs font-medium mb-4 sm:mb-6">
            <i className="ri-calendar-check-line"></i>
            Book Your Consultation
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
            Schedule Your Free Consultation
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto">
            Pick a date and time that works for you. Pay securely in your preferred
            currency and receive instant confirmation.
          </p>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 flex items-center justify-center gap-3 sm:gap-8 overflow-x-auto">
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                selectedDate
                  ? 'bg-emerald-500 text-white'
                  : 'bg-emerald-100 text-emerald-600'
              }`}
            >
              {selectedDate ? <i className="ri-check-line"></i> : '1'}
            </div>
            <span className="text-xs sm:text-sm font-medium text-slate-900 whitespace-nowrap">
              Date &amp; Time
            </span>
          </div>
          <div className="w-6 sm:w-12 h-px bg-gray-200 flex-shrink-0"></div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                formData.name && formData.email
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {formData.name && formData.email ? (
                <i className="ri-check-line"></i>
              ) : (
                '2'
              )}
            </div>
            <span className="text-xs sm:text-sm font-medium text-gray-500 whitespace-nowrap">
              Details
            </span>
          </div>
          <div className="w-6 sm:w-12 h-px bg-gray-200 flex-shrink-0"></div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold bg-gray-100 text-gray-400">
              3
            </div>
            <span className="text-xs sm:text-sm font-medium text-gray-500 whitespace-nowrap">Payment</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-sm text-gray-500">Loading available dates...</p>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <BookingCalendar
                availableSlots={availableSlots}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onSelectDate={handleSelectDate}
                onSelectTime={setSelectedTime}
              />
            </div>
            <div className="lg:col-span-2 space-y-6">
              <CurrencySelector
                selectedCurrency={selectedCurrency}
                onSelectCurrency={setSelectedCurrency}
                convertedAmount={convertedAmount}
                baseAmount={baseAmount}
                isLoading={isConverting}
              />
              <BookingForm
                formData={formData}
                onFormChange={setFormData}
                onSubmit={handleProceedToPayment}
                isReady={isReady}
                isSubmitting={isSubmitting}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
              />
              {errorMsg && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-700 flex items-start gap-2">
                    <i className="ri-error-warning-line text-base mt-0.5"></i>
                    {errorMsg}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Trust badges */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 sm:pb-16">
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-gray-400">
          <div className="flex items-center gap-2">
            <i className="ri-shield-check-line text-lg"></i>
            <span className="text-xs">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-lock-line text-lg"></i>
            <span className="text-xs">SSL Encrypted</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-refund-2-line text-lg"></i>
            <span className="text-xs">Money-Back Guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-customer-service-2-line text-lg"></i>
            <span className="text-xs">24/7 Support</span>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        bookingDetails={bookingDetails}
      />
    </div>
  );
}
